import { useEffect, useMemo, useRef, useState } from "react";
import { LogOut, RefreshCw, Search, Lock, Package, Phone, Mail, MapPin, Download, Send, FileText, FilePlus2, Trash2, TrendingUp, Users, Smartphone, Monitor, Printer, CheckSquare, Square, Calendar, Radio } from "lucide-react";
import { toast } from "sonner";
import {
  adminFetch,
  clearAdminToken,
  getAdminToken,
  setAdminToken,
} from "../lib/adminApi";

const STATUS_LABELS = {
  pending: { label: "În așteptare", color: "bg-amber-100 text-amber-800 border-amber-200" },
  confirmed: { label: "Confirmată", color: "bg-blue-100 text-blue-800 border-blue-200" },
  shipped: { label: "Expediată", color: "bg-purple-100 text-purple-800 border-purple-200" },
  delivered: { label: "Livrată", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled: { label: "Anulată", color: "bg-rose-100 text-rose-800 border-rose-200" },
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());

  useEffect(() => {
    document.title = "Admin · Detereco-Green";
  }, []);

  if (!authed) return <LoginScreen onSuccess={() => setAuthed(true)} />;
  return (
    <AdminDashboard
      onLogout={() => {
        clearAdminToken();
        setAuthed(false);
      }}
    />
  );
}

function LoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Login eșuat");
      }
      const data = await res.json();
      setAdminToken(data.token, data.expires_in);
      toast.success("Autentificare reușită");
      onSuccess();
    } catch (err) {
      toast.error(err.message || "Eroare la autentificare");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-5 py-12">
      <form
        onSubmit={handleSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white rounded-3xl border border-sky-100 shadow-xl p-8 md:p-10"
      >
        <div className="w-14 h-14 rounded-2xl bg-cyan-600 flex items-center justify-center mb-5">
          <Lock className="w-6 h-6 text-white" />
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-black text-slate-900">
          Panou administrare
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Introdu parola pentru a vedea comenzile.
        </p>

        <label className="mt-7 block text-xs font-bold uppercase tracking-wider text-slate-700">
          Parolă admin
        </label>
        <input
          type="password"
          required
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-sm"
        />

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 w-full h-12 rounded-xl bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white font-bold transition-colors"
        >
          {loading ? "Se verifică..." : "Intră în panou"}
        </button>
      </form>
    </main>
  );
}

function AdminDashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [monthStats, setMonthStats] = useState(null);

  const _monthRange = () => {
    const now = new Date();
    const first = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
    const today = now.toISOString().slice(0, 10);
    return { first, today };
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const { first, today } = _monthRange();
      const [statsRes, trafficRes, ordersRes, monthRes] = await Promise.all([
        adminFetch("/api/admin/stats"),
        adminFetch("/api/admin/traffic").catch(() => null),
        adminFetch(
          `/api/admin/orders?limit=200${statusFilter ? `&status=${statusFilter}` : ""}`,
        ),
        adminFetch(`/api/admin/stats?from=${first}&to=${today}`).catch(() => null),
      ]);
      setStats(statsRes);
      setTraffic(trafficRes);
      setOrders(ordersRes.items || []);
      setMonthStats(monthRes);
    } catch (err) {
      toast.error(err.message || "Eroare la încărcare");
      if (err.message?.includes("Sesiune")) onLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Auto-refresh every 30 seconds — silent background update so new orders &
  // traffic appear without manual reload. Pauses while a drawer is open.
  useEffect(() => {
    const id = setInterval(() => {
      if (selected) return; // don't reload while user is reviewing a single order
      refresh();
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, selected]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(
      (o) =>
        o.order_number?.toLowerCase().includes(q) ||
        o.customer?.fullName?.toLowerCase().includes(q) ||
        o.customer?.phone?.includes(q) ||
        o.cargus_awb?.includes(q) ||
        o.oblio_invoice?.number?.includes(q),
    );
  }, [orders, search]);

  const handleStatusChange = async (orderNumber, status) => {
    try {
      await adminFetch(`/api/admin/orders/${orderNumber}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      toast.success("Status actualizat");
      refresh();
      if (selected?.order_number === orderNumber) {
        setSelected({ ...selected, status });
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteOrder = async (orderNumber) => {
    if (
      !window.confirm(
        `Sigur ștergi comanda ${orderNumber}? Această acțiune este permanentă!`,
      )
    ) {
      return;
    }
    try {
      await adminFetch(`/api/admin/orders/${orderNumber}`, {
        method: "DELETE",
      });
      toast.success(`Comanda ${orderNumber} ștearsă`);
      setSelected(null);
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <main className="px-4 md:px-8 py-6 md:py-10 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-black text-slate-900">
            Panou administrare
          </h1>
          <p className="text-sm text-slate-500">Detereco-Green · Comenzi</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="admin-refresh"
            className="h-10 px-4 rounded-lg bg-white border border-slate-200 hover:border-cyan-500 text-sm font-semibold inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Reîncarcă
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-rose-600 text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <StatCard label="Total comenzi" value={stats.total_orders} color="bg-slate-900" />
          <StatCard
            label="Venit total"
            value={`${stats.total_revenue} lei`}
            color="bg-emerald-600"
          />
          <StatCard
            label="În așteptare"
            value={stats.by_status?.pending?.count || 0}
            color="bg-amber-500"
          />
          <StatCard
            label="Livrate"
            value={stats.by_status?.delivered?.count || 0}
            color="bg-cyan-600"
          />
        </div>
      )}

      {/* Current month card (resets each 1st of month) */}
      {monthStats && <CurrentMonthCard stats={monthStats} />}

      {/* Statistici pe zi */}
      <DayStatsCard onLogout={onLogout} />

      {/* Storno invoices */}
      <StornoSection onLogout={onLogout} onChange={refresh} />

      {/* Produse (catalog) */}
      <ProductsAdminSection onLogout={onLogout} />

      {/* Live viewers (real-time count of people on the site) */}
      <LiveVisitorsCard onLogout={onLogout} />

      {/* Traffic Dashboard */}
      {traffic && <TrafficDashboard traffic={traffic} />}

      {/* Bulk action bar (visible when items are selected) */}
      {selectedIds.size > 0 && (
        <div
          data-testid="admin-bulk-action-bar"
          className="sticky top-2 z-20 bg-cyan-900 text-white rounded-2xl border border-cyan-700 shadow-lg flex flex-wrap items-center justify-between gap-3 px-4 py-3 mb-3"
        >
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-cyan-200" />
            <span className="font-bold text-sm">
              {selectedIds.size} {selectedIds.size === 1 ? "comandă" : "comenzi"} selectate
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-cyan-100 hover:text-white underline font-medium"
            >
              Anulează
            </button>
            <BulkPrintButton
              testid="admin-bulk-print"
              label="Printează AWB-uri"
              endpoint="/api/admin/orders/bulk-awb-pdf"
              orderNumbers={Array.from(selectedIds)}
              onDone={() => setSelectedIds(new Set())}
            />
            <BulkPrintButton
              testid="admin-bulk-print-invoices"
              label="Printează facturi"
              endpoint="/api/admin/orders/bulk-invoice-pdf"
              orderNumbers={Array.from(selectedIds)}
              variant="emerald"
              onDone={() => setSelectedIds(new Set())}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Caută după nr. comandă, nume, telefon, AWB..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm bg-white"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="admin-status-filter"
          className="h-10 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm bg-white"
        >
          <option value="">Toate statusurile</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>

      {/* Orders table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3 font-semibold w-10">
                  <button
                    onClick={() => {
                      const allIds = filtered
                        .filter((o) => o.cargus_awb || (o.cargus_awbs?.length || 0) > 0)
                        .map((o) => o.order_number);
                      const allSelected =
                        allIds.length > 0 &&
                        allIds.every((id) => selectedIds.has(id));
                      setSelectedIds(
                        allSelected ? new Set() : new Set(allIds),
                      );
                    }}
                    data-testid="admin-select-all"
                    className="inline-flex items-center justify-center w-5 h-5 rounded text-cyan-600 hover:text-cyan-800 transition-colors"
                    title="Selectează toate cele cu AWB"
                  >
                    {filtered.length > 0 &&
                    filtered
                      .filter((o) => o.cargus_awb)
                      .every((o) => selectedIds.has(o.order_number)) &&
                    selectedIds.size > 0 ? (
                      <CheckSquare className="w-5 h-5" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">Comandă</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Telefon</th>
                <th className="px-4 py-3 font-semibold">Total</th>
                <th className="px-4 py-3 font-semibold">AWB</th>
                <th className="px-4 py-3 font-semibold">Factură</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Data</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-slate-400">
                    <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    Nu există comenzi încă.
                  </td>
                </tr>
              )}
              {filtered.map((o) => {
                const s = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
                const hasAwb = !!o.cargus_awb || (o.cargus_awbs?.length || 0) > 0;
                const isSelected = selectedIds.has(o.order_number);
                return (
                  <tr
                    key={o.order_number}
                    data-testid={`admin-order-row-${o.order_number}`}
                    className={`border-b border-slate-100 hover:bg-cyan-50/50 transition-colors ${
                      isSelected ? "bg-cyan-50" : ""
                    }`}
                  >
                    <td
                      className="px-3 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        disabled={!hasAwb}
                        onClick={() => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(o.order_number)) next.delete(o.order_number);
                            else next.add(o.order_number);
                            return next;
                          });
                        }}
                        data-testid={`admin-select-${o.order_number}`}
                        title={hasAwb ? "Selectează" : "Fără AWB — nu poate fi printat"}
                        className={`inline-flex items-center justify-center w-5 h-5 rounded transition-colors ${
                          hasAwb
                            ? "text-cyan-600 hover:text-cyan-800 cursor-pointer"
                            : "text-slate-300 cursor-not-allowed"
                        }`}
                      >
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                    <td
                      className="px-4 py-3 font-mono font-bold text-slate-900 cursor-pointer"
                      onClick={() => setSelected(o)}
                    >
                      {o.order_number}
                    </td>
                    <td
                      className="px-4 py-3 font-semibold cursor-pointer"
                      onClick={() => setSelected(o)}
                    >
                      {o.customer?.fullName}
                    </td>
                    <td
                      className="px-4 py-3 text-slate-700 cursor-pointer"
                      onClick={() => setSelected(o)}
                    >
                      {o.customer?.phone}
                    </td>
                    <td
                      className="px-4 py-3 font-bold text-cyan-700 cursor-pointer"
                      onClick={() => setSelected(o)}
                    >
                      {o.total} lei
                    </td>
                    <td
                      className="px-4 py-3 font-mono text-xs text-slate-600 cursor-pointer"
                      onClick={() => setSelected(o)}
                    >
                      {o.cargus_awbs?.length > 1 ? (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 border border-cyan-200 font-bold">
                          {o.cargus_awbs.length} AWB
                        </span>
                      ) : (
                        o.cargus_awb || "—"
                      )}
                    </td>
                    <td
                      className="px-4 py-3 font-mono text-xs cursor-pointer"
                      onClick={() => setSelected(o)}
                    >
                      {o.oblio_invoice?.number ? (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-bold">
                          {o.oblio_invoice.series} {o.oblio_invoice.number}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td
                      className="px-4 py-3 cursor-pointer"
                      onClick={() => setSelected(o)}
                    >
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold border ${s.color}`}
                      >
                        {s.label}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3 text-slate-500 text-xs cursor-pointer"
                      onClick={() => setSelected(o)}
                    >
                      {formatDate(o.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <OrderDetailDrawer
          order={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
          onDelete={handleDeleteOrder}
        />
      )}
    </main>
  );
}

function LiveVisitorsCard({ onLogout }) {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await adminFetch("/api/admin/traffic/live");
        if (cancelled) return;
        setData(res);
        setPulse(true);
        setTimeout(() => !cancelled && setPulse(false), 500);
      } catch (err) {
        if (cancelled) return;
        if (err.message?.includes("Sesiune")) onLogout?.();
      }
    };

    load();
    const t = setInterval(load, 15_000); // Refresh every 15 s
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [onLogout]);

  const live = data?.live_count ?? 0;
  const mobile = data?.mobile ?? 0;
  const desktop = data?.desktop ?? 0;
  const totalUnique = data?.total_unique_visitors ?? 0;

  return (
    <section
      data-testid="admin-live-visitors"
      className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-cyan-50 p-5 mb-6"
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Radio className="w-5 h-5 text-emerald-600" />
          <span
            className={
              "absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-500 " +
              (live > 0 ? "animate-ping" : "opacity-30")
            }
          />
          <span
            className={
              "absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full " +
              (live > 0 ? "bg-emerald-600" : "bg-slate-400")
            }
          />
        </div>
        <h2 className="font-heading font-black text-slate-900">Vizitatori LIVE</h2>
        <span className="text-[11px] text-slate-500 ml-1">
          se actualizează automat la fiecare 15 secunde
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div
          className={
            "rounded-2xl p-4 text-white transition-all duration-500 " +
            (live > 0
              ? "bg-gradient-to-br from-emerald-500 to-emerald-700"
              : "bg-gradient-to-br from-slate-500 to-slate-700")
          }
        >
          <div className="flex items-center gap-2">
            <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-100">
              Live acum
            </div>
            {live > 0 && (
              <span className="ml-auto inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
            )}
          </div>
          <div
            data-testid="live-count"
            className={
              "text-4xl font-black mt-1 " + (pulse ? "scale-110" : "scale-100")
            }
            style={{ transition: "transform 0.3s ease-out" }}
          >
            {live}
          </div>
          <div className="text-[11px] text-emerald-100 mt-1">
            {live === 1
              ? "persoană pe site"
              : live === 0
                ? "nimeni pe site acum"
                : "persoane pe site"}
          </div>
        </div>

        <div className="rounded-2xl bg-cyan-600 text-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-cyan-100 font-bold">
            Pe mobil
          </div>
          <div className="text-3xl font-black mt-1 flex items-center gap-2">
            <Smartphone className="w-6 h-6 opacity-80" />
            {mobile}
          </div>
        </div>

        <div className="rounded-2xl bg-slate-800 text-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-300 font-bold">
            Pe desktop
          </div>
          <div className="text-3xl font-black mt-1 flex items-center gap-2">
            <Monitor className="w-6 h-6 opacity-80" />
            {desktop}
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-indigo-100 font-bold">
            Total vizitatori unici
          </div>
          <div className="text-3xl font-black mt-1 flex items-center gap-2">
            <Users className="w-6 h-6 opacity-80" />
            {Number(totalUnique).toLocaleString("ro-RO")}
          </div>
          <div className="text-[11px] text-indigo-100 mt-1">de la lansarea site-ului</div>
        </div>
      </div>
    </section>
  );
}

function CurrentMonthCard({ stats }) {
  const now = new Date();
  const monthName = now.toLocaleDateString("ro-RO", { month: "long", year: "numeric" });
  const orders = stats?.total_orders ?? 0;
  const gross = stats?.total_revenue ?? 0;
  const stornoTotal = stats?.storno_total ?? 0;
  const stornoCount = stats?.storno_count ?? 0;
  const net = stats?.net_revenue ?? gross;
  return (
    <section
      data-testid="admin-current-month"
      className="rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-emerald-50 p-5 mb-6"
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-cyan-700" />
        <h2 className="font-heading font-black text-slate-900 capitalize">
          Luna curentă · {monthName}
        </h2>
        <span className="text-[11px] text-slate-500 ml-1">
          se resetează automat pe 1 ale lunii următoare
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-slate-900 text-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-300 font-semibold">
            Comenzi în lună
          </div>
          <div className="text-3xl font-black mt-1" data-testid="month-orders-count">
            {orders}
          </div>
        </div>
        <div className="rounded-2xl bg-emerald-600 text-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-emerald-100 font-semibold">
            Venit brut
          </div>
          <div className="text-3xl font-black mt-1">
            {Number(gross).toLocaleString("ro-RO")} lei
          </div>
        </div>
        <div className="rounded-2xl bg-rose-500 text-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-rose-100 font-semibold">
            Storno {stornoCount ? `(${stornoCount})` : ""}
          </div>
          <div className="text-3xl font-black mt-1" data-testid="month-storno-total">
            − {Number(stornoTotal).toLocaleString("ro-RO")} lei
          </div>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-cyan-600 to-cyan-800 text-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-cyan-100 font-semibold">
            Venit net
          </div>
          <div className="text-3xl font-black mt-1" data-testid="month-net-revenue">
            {Number(net).toLocaleString("ro-RO")} lei
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductsAdminSection({ onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // slug of product being edited, or "new"

  const emptyProduct = {
    slug: "",
    title: "",
    short: "",
    price: 0,
    oldPrice: null,
    discount: null,
    image: "",
    badge: "",
    highlights: "",
    description: "",
  };

  const load = async () => {
    setLoading(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/products`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      toast.error("Eroare la încărcare produse");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Ștergi produsul "${slug}"?`)) return;
    try {
      await adminFetch(`/api/admin/products/${slug}`, { method: "DELETE" });
      toast.success("Produs șters");
      await load();
    } catch (err) {
      toast.error(err.message || "Eroare");
      if (err.message?.includes("Sesiune")) onLogout?.();
    }
  };

  return (
    <section
      data-testid="admin-products-section"
      className="bg-white rounded-2xl border border-slate-200 p-5 mb-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-emerald-600" />
          <h2 className="font-heading font-black text-slate-900">Produse (catalog)</h2>
          <span className="text-[11px] text-slate-400 ml-1">
            {items.length} produse · adaugă / editează / șterge
          </span>
        </div>
        <button
          onClick={() => setEditing({ ...emptyProduct })}
          data-testid="product-add-btn"
          className="h-9 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold inline-flex items-center gap-1.5"
        >
          <FilePlus2 className="w-4 h-4" />
          Adaugă produs
        </button>
      </div>

      {editing && (
        <ProductForm
          initial={editing}
          isNew={!editing.slug || editing._isNew}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
            // Ask context to refresh so public site sees changes immediately
            window.dispatchEvent(new Event("products:reload"));
          }}
        />
      )}

      {loading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Se încarcă...</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold w-16">Imagine</th>
                <th className="text-left px-3 py-2 font-semibold">Titlu</th>
                <th className="text-left px-3 py-2 font-semibold">Slug</th>
                <th className="text-left px-3 py-2 font-semibold">Preț</th>
                <th className="text-right px-3 py-2 font-semibold">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.slug} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    {p.image ? (
                      <img
                        alt=""
                        src={p.image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${p.image}` : p.image}
                        className="w-12 h-12 object-cover rounded-lg border border-slate-200"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-slate-100" />
                    )}
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-800 max-w-xs truncate">{p.title}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 font-mono">{p.slug}</td>
                  <td className="px-3 py-2 font-bold text-emerald-700">{p.price} lei</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing({ ...p, highlights: (p.highlights || []).join("\n") })}
                      className="text-cyan-700 hover:text-cyan-900 text-xs font-bold mr-3"
                      data-testid={`product-edit-${p.slug}`}
                    >
                      Editează
                    </button>
                    <button
                      onClick={() => handleDelete(p.slug)}
                      className="text-rose-600 hover:text-rose-800 text-xs font-bold"
                      data-testid={`product-delete-${p.slug}`}
                    >
                      Șterge
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-slate-400 italic text-xs">
                    Nicio produs în catalog
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ProductForm({ initial, isNew, onCancel, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setF((prev) => ({ ...prev, [k]: v }));
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/admin/products/upload-image`, {
        method: "POST",
        headers: { "X-Admin-Token": getAdminToken() },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setF((prev) => ({ ...prev, image: data.url }));
      toast.success("Imagine încărcată");
    } catch (err) {
      toast.error(err.message || "Eroare upload");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.slug || !f.title || !f.price) {
      toast.error("Slug, titlu și preț sunt obligatorii");
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...f,
        price: parseFloat(f.price),
        oldPrice: f.oldPrice ? parseFloat(f.oldPrice) : null,
        discount: f.discount ? parseInt(f.discount) : null,
        highlights: typeof f.highlights === "string"
          ? f.highlights.split("\n").map((s) => s.trim()).filter(Boolean)
          : f.highlights,
      };
      if (isNew) {
        await adminFetch("/api/admin/products", { method: "POST", body: JSON.stringify(body) });
        toast.success("Produs adăugat");
      } else {
        await adminFetch(`/api/admin/products/${f.slug}`, { method: "PUT", body: JSON.stringify(body) });
        toast.success("Produs actualizat");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Eroare");
    } finally {
      setBusy(false);
    }
  };

  const imgUrl = f.image?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${f.image}` : f.image;

  return (
    <form
      onSubmit={submit}
      data-testid="product-form"
      className="mb-5 p-4 rounded-2xl bg-emerald-50/60 border-2 border-emerald-200"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-black text-emerald-800 uppercase tracking-wider">
          {isNew ? "Produs nou" : `Editare · ${f.slug}`}
        </div>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
          Anulează
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <input type="text" value={f.slug} onChange={set("slug")} disabled={!isNew} required placeholder="slug (unic, fara spatii) *"
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm disabled:bg-slate-100" />
        <input type="text" value={f.title} onChange={set("title")} required placeholder="Titlu *"
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />
        <input type="text" value={f.short || ""} onChange={set("short")} placeholder="Descriere scurtă"
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm md:col-span-2" />
        <input type="number" step="0.01" value={f.price} onChange={set("price")} required placeholder="Preț (lei) *"
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />
        <input type="number" step="0.01" value={f.oldPrice || ""} onChange={set("oldPrice")} placeholder="Preț vechi (opțional)"
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />
        <input type="number" value={f.discount || ""} onChange={set("discount")} placeholder="Discount %"
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />
        <input type="text" value={f.badge || ""} onChange={set("badge")} placeholder="Badge (ex: Cel mai bun preț)"
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />

        <div className="md:col-span-2">
          <label className="text-[11px] uppercase text-slate-500 font-semibold block mb-1">Imagine</label>
          <div className="flex items-start gap-3">
            {imgUrl && (
              <img alt="preview" src={imgUrl} className="w-24 h-24 object-cover rounded-lg border-2 border-emerald-200" />
            )}
            <div className="flex-1 space-y-2">
              <input type="text" value={f.image || ""} onChange={set("image")} placeholder="URL imagine (sau upload mai jos)"
                className="w-full h-9 px-3 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-xs font-mono" />
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0])}
                  className="text-xs file:mr-2 file:h-8 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-emerald-100 file:text-emerald-800 file:cursor-pointer" />
                {uploading && <span className="text-xs text-slate-500">Se încarcă...</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="text-[11px] uppercase text-slate-500 font-semibold block mb-1">
            Highlights (câte unul pe linie)
          </label>
          <textarea value={f.highlights || ""} onChange={set("highlights")} rows={4} placeholder="- Peste 300 spălări&#10;- Transport gratuit&#10;- ..."
            className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />
        </div>

        <div className="md:col-span-2">
          <label className="text-[11px] uppercase text-slate-500 font-semibold block mb-1">Descriere lungă</label>
          <textarea value={f.description || ""} onChange={set("description")} rows={4}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button type="submit" disabled={busy || uploading} data-testid="product-form-submit"
          className="h-11 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-bold text-sm inline-flex items-center gap-2">
          <FilePlus2 className="w-4 h-4" />
          {busy ? "Se salvează..." : isNew ? "Adaugă produs" : "Salvează modificările"}
        </button>
        <span className="text-[11px] text-slate-500">
          Slug-ul e cheia unică — nu se poate schimba după creare.
        </span>
      </div>
    </form>
  );
}

function StornoSection({ onLogout, onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [issuedAt, setIssuedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const res = await adminFetch("/api/admin/storno");
      setItems(res.items || []);
      setTotal(res.total_amount || 0);
    } catch (err) {
      if (err.message?.includes("Sesiune")) onLogout?.();
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Selectează un fișier PDF");
      return;
    }
    const parsed = parseFloat(String(amount).replace(",", "."));
    if (!parsed || parsed <= 0) {
      toast.error("Introdu suma stornată");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("amount", String(parsed));
      fd.append("note", note);
      fd.append("order_number", orderNumber);
      fd.append("issued_at", `${issuedAt}T12:00:00`);
      fd.append("pdf", file);
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/admin/storno`, {
        method: "POST",
        headers: { "X-Admin-Token": getAdminToken() },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success("Storno încărcat");
      setAmount("");
      setNote("");
      setOrderNumber("");
      setFile(null);
      const fi = document.getElementById("storno-file-input");
      if (fi) fi.value = "";
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.message || "Eroare la încărcare");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ștergi această factură storno? Se va re-adăuga suma la venit.")) return;
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/admin/storno/${id}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": getAdminToken() },
      });
      if (!res.ok) throw new Error("Eroare la ștergere");
      toast.success("Storno șters");
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openPdf = (id) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    // The endpoint requires X-Admin-Token header, so we fetch then blob-open
    fetch(`${apiBase}/api/admin/storno/${id}/pdf`, {
      headers: { "X-Admin-Token": getAdminToken() },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("PDF indisponibil"))))
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      })
      .catch(() => toast.error("Nu s-a putut deschide PDF-ul"));
  };

  return (
    <section
      data-testid="admin-storno-section"
      className="bg-white rounded-2xl border border-slate-200 p-5 mb-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-rose-600" />
          <h2 className="font-heading font-black text-slate-900">Facturi storno</h2>
          <span className="text-[11px] text-slate-400 ml-1">
            se scad automat din venit
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-2.5 py-1 rounded-full"
            data-testid="storno-total-badge"
          >
            Total: {Number(total).toLocaleString("ro-RO")} lei · {items.length}{" "}
            {items.length === 1 ? "factură" : "facturi"}
          </span>
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="storno-toggle-btn"
            className="text-xs font-bold text-cyan-700 hover:text-cyan-900 underline"
          >
            {expanded ? "Ascunde" : "Adaugă / Vezi toate"}
          </button>
        </div>
      </div>

      {expanded && (
        <>
          <form
            onSubmit={handleUpload}
            className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-5 p-4 rounded-2xl bg-rose-50/60 border border-rose-100"
            data-testid="storno-upload-form"
          >
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">
                Sumă stornată (lei) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                data-testid="storno-amount-input"
                className="w-full h-10 px-3 rounded-xl border border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none text-sm font-medium"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">
                Data emitere
              </label>
              <input
                type="date"
                value={issuedAt}
                onChange={(e) => setIssuedAt(e.target.value)}
                data-testid="storno-date-input"
                className="w-full h-10 px-3 rounded-xl border border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none text-sm font-medium"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">
                Nr. comandă (opțional)
              </label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="DG..."
                data-testid="storno-order-input"
                className="w-full h-10 px-3 rounded-xl border border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none text-sm font-medium"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">
                PDF factură storno *
              </label>
              <input
                id="storno-file-input"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                required
                data-testid="storno-file-input"
                className="w-full text-xs file:mr-2 file:h-9 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-rose-100 file:text-rose-800 file:cursor-pointer"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">
                Notă (opțional)
              </label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Motiv storno..."
                data-testid="storno-note-input"
                className="w-full h-10 px-3 rounded-xl border border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none text-sm font-medium"
              />
            </div>
            <div className="md:col-span-6 flex items-center gap-3">
              <button
                type="submit"
                disabled={uploading}
                data-testid="storno-submit-btn"
                className="h-11 px-5 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 text-white font-bold text-sm transition-colors flex items-center gap-2"
              >
                <FilePlus2 className="w-4 h-4" />
                {uploading ? "Se încarcă..." : "Adaugă storno"}
              </button>
              <span className="text-[11px] text-slate-500">
                PDF max 10 MB. Suma se va scădea din venitul intervalului selectat.
              </span>
            </div>
          </form>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Data</th>
                  <th className="text-left px-3 py-2 font-semibold">Sumă</th>
                  <th className="text-left px-3 py-2 font-semibold">Comandă</th>
                  <th className="text-left px-3 py-2 font-semibold">Notă</th>
                  <th className="text-left px-3 py-2 font-semibold">Fișier</th>
                  <th className="text-right px-3 py-2 font-semibold">Acțiuni</th>
                </tr>
              </thead>
              <tbody data-testid="storno-list">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-slate-400 italic text-xs">
                      Nicio factură storno încărcată încă
                    </td>
                  </tr>
                ) : (
                  items.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs text-slate-700">
                        {formatDate(s.issued_at)}
                      </td>
                      <td className="px-3 py-2 font-bold text-rose-700">
                        − {Number(s.amount).toLocaleString("ro-RO")} lei
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700">
                        {s.order_number || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 max-w-[220px] truncate">
                        {s.note || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => openPdf(s.id)}
                          className="text-cyan-700 hover:text-cyan-900 underline text-xs font-medium inline-flex items-center gap-1"
                          data-testid={`storno-view-${s.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                          {s.filename}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => handleDelete(s.id)}
                          className="text-rose-600 hover:text-rose-800 text-xs font-bold inline-flex items-center gap-1"
                          data-testid={`storno-delete-${s.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Șterge
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function DayStatsCard({ onLogout }) {
  // Default: 1st of current month → today
  const today = new Date();
  const isoToday = today.toISOString().slice(0, 10);
  const isoFirst = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;

  const [from, setFrom] = useState(isoFirst);
  const [to, setTo] = useState(isoToday);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStats = async (f, t) => {
    if (!f || !t) return;
    setLoading(true);
    try {
      const res = await adminFetch(`/api/admin/stats?from=${f}&to=${t}`);
      setData(res);
    } catch (err) {
      toast.error(err.message || "Eroare la încărcare");
      if (err.message?.includes("Sesiune")) onLogout?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const setThisMonth = () => {
    const now = new Date();
    setFrom(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`);
    setTo(now.toISOString().slice(0, 10));
  };
  const setLastMonth = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    setFrom(first.toISOString().slice(0, 10));
    setTo(last.toISOString().slice(0, 10));
  };
  const setToday = () => {
    setFrom(isoToday);
    setTo(isoToday);
  };
  const setLast7 = () => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    setFrom(d.toISOString().slice(0, 10));
    setTo(isoToday);
  };

  const totalCount = data?.total_orders ?? 0;
  const totalRevenue = data?.total_revenue ?? 0;
  const pretty = (iso) =>
    iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" }) : "";
  const label = from === to ? pretty(from) : `${pretty(from)} → ${pretty(to)}`;

  return (
    <section
      data-testid="admin-day-stats"
      className="bg-white rounded-2xl border border-slate-200 p-5 mb-6"
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-cyan-600" />
        <h2 className="font-heading font-black text-slate-900">Statistici pe interval</h2>
        <span className="text-[11px] text-slate-400 ml-1">
          alege perioada (de la → până la) pentru a vedea comenzile și venitul
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex flex-col">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
            De la
          </label>
          <input
            type="date"
            value={from}
            max={to || isoToday}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="admin-range-from"
            className="h-10 px-3 rounded-xl border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-sm font-medium text-slate-900"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
            Până la
          </label>
          <input
            type="date"
            value={to}
            min={from}
            max={isoToday}
            onChange={(e) => setTo(e.target.value)}
            data-testid="admin-range-to"
            className="h-10 px-3 rounded-xl border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-sm font-medium text-slate-900"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-5">
          <button
            onClick={setToday}
            data-testid="admin-range-today-btn"
            className="h-10 px-3 rounded-xl bg-cyan-50 hover:bg-cyan-100 text-cyan-700 text-xs font-bold border border-cyan-200 transition-colors"
          >
            Azi
          </button>
          <button
            onClick={setLast7}
            data-testid="admin-range-7d-btn"
            className="h-10 px-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-bold border border-slate-200 transition-colors"
          >
            Ultimele 7 zile
          </button>
          <button
            onClick={setThisMonth}
            data-testid="admin-range-month-btn"
            className="h-10 px-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold border border-emerald-200 transition-colors"
          >
            Luna curentă
          </button>
          <button
            onClick={setLastMonth}
            data-testid="admin-range-last-month-btn"
            className="h-10 px-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-bold border border-slate-200 transition-colors"
          >
            Luna trecută
          </button>
          {loading && <span className="text-xs text-slate-500 ml-1">Se încarcă...</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div
          data-testid="admin-day-orders-count"
          className="rounded-2xl bg-gradient-to-br from-cyan-600 to-cyan-700 text-white p-4"
        >
          <div className="text-xs uppercase tracking-wider text-cyan-100 font-semibold">
            Comenzi în perioada
          </div>
          <div className="text-3xl font-black mt-1">{totalCount}</div>
          <div className="text-[11px] text-cyan-100 mt-1">{label}</div>
        </div>
        <div
          data-testid="admin-day-revenue"
          className="rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-4"
        >
          <div className="text-xs uppercase tracking-wider text-emerald-100 font-semibold">
            Venit
          </div>
          <div className="text-3xl font-black mt-1">
            {Number(totalRevenue).toLocaleString("ro-RO")} lei
          </div>
          <div className="text-[11px] text-emerald-100 mt-1">{label}</div>
        </div>
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Pe status
          </div>
          {Object.keys(data?.by_status || {}).length === 0 ? (
            <div className="text-xs text-slate-400 italic">Nicio comandă</div>
          ) : (
            <ul className="space-y-1">
              {Object.entries(data?.by_status || {}).map(([status, info]) => (
                <li key={status} className="flex justify-between text-xs">
                  <span className="text-slate-600">
                    {STATUS_LABELS[status]?.label || status}
                  </span>
                  <span className="font-bold text-slate-900">
                    {info.count} · {info.total} lei
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function TrafficDashboard({ traffic }) {
  const tl = traffic.timeline || [];
  const maxViews = Math.max(1, ...tl.map((d) => d.views));
  const totalDevice = (traffic.device?.mobile || 0) + (traffic.device?.desktop || 0);
  const mobilePct = totalDevice
    ? Math.round((traffic.device.mobile / totalDevice) * 100)
    : 0;
  return (
    <section
      data-testid="admin-traffic-dashboard"
      className="bg-white rounded-2xl border border-slate-200 p-5 mb-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-cyan-600" />
        <h2 className="font-heading font-black text-slate-900">Trafic site</h2>
        <span className="text-[11px] text-slate-400 ml-1">
          tracking propriu · GDPR friendly (IP hash)
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <TrafficCell
          label="Astăzi"
          views={traffic.today?.views}
          unique={traffic.today?.unique}
          accent="text-cyan-700"
        />
        <TrafficCell
          label="Ultimele 7 zile"
          views={traffic.last_7d?.views}
          unique={traffic.last_7d?.unique}
          accent="text-emerald-700"
        />
        <TrafficCell
          label="Ultimele 30 zile"
          views={traffic.last_30d?.views}
          unique={traffic.last_30d?.unique}
          accent="text-amber-700"
        />
        <TrafficCell
          label="Total"
          views={traffic.total?.views}
          unique={traffic.total?.unique}
          accent="text-slate-900"
        />
      </div>

      {/* Mini timeline chart - last 14 days */}
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Ultimele 14 zile
          </div>
          <div className="text-[11px] text-slate-400">
            <span className="inline-block w-2 h-2 rounded-sm bg-cyan-500 mr-1" />
            vizualizări
            <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500 ml-3 mr-1" />
            unici
          </div>
        </div>
        <div className="flex items-end gap-1 h-24">
          {tl.map((d) => {
            const h = Math.round((d.views / maxViews) * 88);
            const uh = Math.round((d.unique / maxViews) * 88);
            return (
              <div
                key={d.day}
                className="flex-1 flex flex-col items-center gap-0.5 group relative"
                title={`${d.day}: ${d.views} vizualizări / ${d.unique} unici`}
              >
                <div className="w-full flex items-end justify-center gap-px h-[88px]">
                  <div
                    className="w-1/2 bg-cyan-500 rounded-t-sm transition-all group-hover:bg-cyan-600"
                    style={{ height: `${h}px` }}
                  />
                  <div
                    className="w-1/2 bg-emerald-500 rounded-t-sm transition-all group-hover:bg-emerald-600"
                    style={{ height: `${uh}px` }}
                  />
                </div>
                <div className="text-[9px] text-slate-400 font-mono">
                  {d.day.slice(8)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Top pages */}
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
            Top pagini · 30 zile
          </div>
          <div className="space-y-2 text-sm">
            {(traffic.top_pages || []).slice(0, 6).map((p) => (
              <div
                key={p.page}
                className="flex items-center justify-between gap-2"
              >
                <span className="font-mono text-xs text-slate-700 truncate flex-1">
                  {p.page || "/"}
                </span>
                <span className="font-bold text-cyan-700 shrink-0">
                  {p.count}
                </span>
              </div>
            ))}
            {(!traffic.top_pages || traffic.top_pages.length === 0) && (
              <div className="text-xs text-slate-400">Niciun trafic încă.</div>
            )}
          </div>
        </div>

        {/* Top referrers */}
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
            Top surse · 30 zile
          </div>
          <div className="space-y-2 text-sm">
            {(traffic.top_referrers || []).slice(0, 6).map((r) => {
              let host = r.referrer;
              try {
                host = new URL(r.referrer).hostname;
              } catch {
                /* ignore */
              }
              return (
                <div
                  key={r.referrer}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="text-xs text-slate-700 truncate flex-1">
                    {host}
                  </span>
                  <span className="font-bold text-emerald-700 shrink-0">
                    {r.count}
                  </span>
                </div>
              );
            })}
            {(!traffic.top_referrers || traffic.top_referrers.length === 0) && (
              <div className="text-xs text-slate-400">
                Vizitatorii vin direct (fără referrer).
              </div>
            )}
          </div>
        </div>

        {/* Devices */}
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
            Dispozitive · 30 zile
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="inline-flex items-center gap-1.5 text-slate-700">
                  <Smartphone className="w-3.5 h-3.5" /> Mobile
                </span>
                <span className="font-bold text-cyan-700">
                  {traffic.device?.mobile || 0} ({mobilePct}%)
                </span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-cyan-500 transition-all"
                  style={{ width: `${mobilePct}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="inline-flex items-center gap-1.5 text-slate-700">
                  <Monitor className="w-3.5 h-3.5" /> Desktop
                </span>
                <span className="font-bold text-slate-700">
                  {traffic.device?.desktop || 0} ({100 - mobilePct}%)
                </span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-700 transition-all"
                  style={{ width: `${100 - mobilePct}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrafficCell({ label, views, unique, accent }) {
  return (
    <div className="rounded-xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        {label}
      </div>
      <div className={`font-heading text-2xl md:text-3xl font-black mt-1 ${accent}`}>
        {views ?? 0}
      </div>
      <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
        <Users className="w-3 h-3" /> {unique ?? 0} unici
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-start gap-3">
        <div className={`w-1.5 h-12 rounded-full ${color}`} />
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            {label}
          </div>
          <div className="font-heading text-xl md:text-2xl font-black text-slate-900 mt-0.5">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function DownloadAwbButton({ orderNumber, awb }) {
  const [loading, setLoading] = useState(false);
  const handleDownload = async () => {
    setLoading(true);
    try {
      const token = getAdminToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/orders/${orderNumber}/awb-pdf`,
        { headers: { "X-Admin-Token": token || "" } },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AWB-${awb}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("AWB descărcat");
    } catch (err) {
      toast.error("Eroare la descărcare: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      data-testid={`admin-download-awb-${orderNumber}`}
      className="h-10 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white font-bold text-xs inline-flex items-center justify-center gap-1.5 transition-colors"
    >
      <Download className="w-3.5 h-3.5" />
      {loading ? "Se descarcă..." : "Descarcă AWB PDF"}
    </button>
  );
}

function ResendEmailButton({ orderNumber }) {
  const [loading, setLoading] = useState(false);
  const handleResend = async () => {
    setLoading(true);
    try {
      const data = await adminFetch(`/api/admin/orders/${orderNumber}/resend-email`, {
        method: "POST",
      });
      if (data.email_sent) {
        toast.success("Email retrimis cu succes");
      } else {
        toast.error("Email-ul nu a putut fi trimis");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={handleResend}
      disabled={loading}
      data-testid={`admin-resend-email-${orderNumber}`}
      className="h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-bold text-xs inline-flex items-center justify-center gap-1.5 transition-colors"
    >
      <Send className="w-3.5 h-3.5" />
      {loading ? "Se trimite..." : "Retrimite email"}
    </button>
  );
}

function BulkPrintButton({ testid, label, endpoint, orderNumbers, variant = "white", onDone }) {
  const [printing, setPrinting] = useState(false);
  const handleClick = async () => {
    setPrinting(true);
    try {
      const token = getAdminToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ""}${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Admin-Token": token || "",
          },
          body: JSON.stringify({ order_numbers: orderNumbers }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (win) {
        setTimeout(() => {
          try {
            win.focus();
            win.print();
          } catch {
            /* user prints manually */
          }
        }, 800);
      }
      toast.success(`PDF deschis în fereastră nouă (${orderNumbers.length} comenzi)`);
      if (onDone) onDone();
    } catch (err) {
      toast.error("Eroare PDF: " + err.message);
    } finally {
      setPrinting(false);
    }
  };
  const styles =
    variant === "emerald"
      ? "bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-500 text-white"
      : "bg-white hover:bg-cyan-50 disabled:bg-slate-300 text-cyan-900";
  return (
    <button
      disabled={printing}
      data-testid={testid}
      onClick={handleClick}
      className={`h-10 px-4 rounded-lg font-bold text-sm inline-flex items-center gap-2 transition-colors ${styles}`}
    >
      <Printer className="w-4 h-4" />
      {printing ? "Se pregătește..." : label}
    </button>
  );
}

function DownloadInvoiceButton({ orderNumber, label }) {
  const [loading, setLoading] = useState(false);
  const handleDownload = async () => {
    setLoading(true);
    try {
      const token = getAdminToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/orders/${orderNumber}/invoice-pdf`,
        { headers: { "X-Admin-Token": token || "" } },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Factura-${label || orderNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Factură descărcată");
    } catch (err) {
      toast.error("Eroare la descărcare: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      data-testid={`admin-download-invoice-${orderNumber}`}
      className="h-10 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 text-white font-bold text-xs inline-flex items-center justify-center gap-1.5 transition-colors"
    >
      <FileText className="w-3.5 h-3.5" />
      {loading ? "Se descarcă..." : "Descarcă factură"}
    </button>
  );
}

function GenerateInvoiceButton({ orderNumber, onDone }) {
  const [loading, setLoading] = useState(false);
  const handleGenerate = async () => {
    setLoading(true);
    try {
      const data = await adminFetch(
        `/api/admin/orders/${orderNumber}/regenerate-invoice`,
        { method: "POST" },
      );
      toast.success(
        `Factură emisă: ${data.oblio_invoice?.series || ""} ${data.oblio_invoice?.number || ""}`,
      );
      if (onDone) onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={handleGenerate}
      disabled={loading}
      data-testid={`admin-generate-invoice-${orderNumber}`}
      className="h-10 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-bold text-xs inline-flex items-center justify-center gap-1.5 transition-colors"
    >
      <FilePlus2 className="w-3.5 h-3.5" />
      {loading ? "Se emite..." : "Emite factură Oblio"}
    </button>
  );
}

function GenerateAwbButton({ order, label }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const c = order.customer || {};
  const [form, setForm] = useState({
    fullName: c.fullName || "",
    phone: c.phone || "",
    email: c.email || "",
    address: c.address || "",
    city: c.city || "",
    county: c.county || "",
    postalCode: c.postalCode || "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.phone || !form.address || !form.city || !form.county) {
      toast.error("Nume, telefon, adresă, oraș și județ sunt obligatorii");
      return;
    }
    setBusy(true);
    try {
      const res = await adminFetch(
        `/api/admin/orders/${order.order_number}/generate-awb`,
        { method: "POST", body: JSON.stringify(form) },
      );
      toast.success(
        `AWB generat: ${res.cargus_awb}${res.cargus_awbs?.length > 1 ? ` (${res.cargus_awbs.length} colete)` : ""}`,
        { duration: 6000 },
      );
      setOpen(false);
      // Refresh the page data by triggering the parent — cleanest: reload window
      setTimeout(() => window.location.reload(), 700);
    } catch (err) {
      toast.error(err.message || "Cargus a refuzat generarea", { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid={`admin-open-generate-awb-${order.order_number}`}
        className="w-full h-10 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-bold inline-flex items-center justify-center gap-2 transition-colors"
      >
        <FilePlus2 className="w-4 h-4" />
        {label || "Generează AWB"}
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`admin-generate-awb-form-${order.order_number}`}
      className="mt-3 p-3 rounded-xl bg-white border-2 border-cyan-300 space-y-2"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-black text-cyan-700 uppercase tracking-wider">
          Verifică și corectează adresa
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-slate-400 hover:text-slate-700 text-xs font-bold"
        >
          Anulează
        </button>
      </div>

      <input
        type="text"
        value={form.fullName}
        onChange={set("fullName")}
        placeholder="Nume complet"
        data-testid={`gen-awb-fullName-${order.order_number}`}
        className="w-full h-9 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          type="tel"
          value={form.phone}
          onChange={set("phone")}
          placeholder="Telefon"
          data-testid={`gen-awb-phone-${order.order_number}`}
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm"
        />
        <input
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="Email (opțional)"
          data-testid={`gen-awb-email-${order.order_number}`}
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm"
        />
      </div>
      <input
        type="text"
        value={form.address}
        onChange={set("address")}
        placeholder="Stradă, număr, bloc, apartament"
        data-testid={`gen-awb-address-${order.order_number}`}
        className="w-full h-9 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm"
      />
      <div className="grid grid-cols-3 gap-2">
        <input
          type="text"
          value={form.city}
          onChange={set("city")}
          placeholder="Localitate"
          data-testid={`gen-awb-city-${order.order_number}`}
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm"
        />
        <input
          type="text"
          value={form.county}
          onChange={set("county")}
          placeholder="Județ"
          data-testid={`gen-awb-county-${order.order_number}`}
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm"
        />
        <input
          type="text"
          value={form.postalCode}
          onChange={set("postalCode")}
          placeholder="Cod poștal"
          data-testid={`gen-awb-postal-${order.order_number}`}
          className="h-9 px-3 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-sm"
        />
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid={`admin-submit-generate-awb-${order.order_number}`}
        className="w-full h-10 rounded-xl bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-300 text-white text-xs font-bold inline-flex items-center justify-center gap-2 transition-colors"
      >
        <FilePlus2 className="w-4 h-4" />
        {busy ? "Se generează..." : "Generează AWB acum"}
      </button>
    </form>
  );
}

function OrderDetailDrawer({ order, onClose, onStatusChange, onDelete }) {
  const c = order.customer || {};
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end md:items-center md:justify-end"
      onClick={onClose}
    >
      <div
        className="bg-white w-full md:max-w-lg md:h-full rounded-t-3xl md:rounded-none overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-order-detail"
      >
        <div className="px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Comandă
              </div>
              <div className="font-heading text-lg font-black text-slate-900 font-mono">
                {order.order_number}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600"
              aria-label="Închide"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-5 py-5 space-y-5">
          {/* Status update */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Actualizează status
            </div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => onStatusChange(order.order_number, k)}
                  data-testid={`admin-set-status-${k}`}
                  className={`h-9 rounded-lg border text-xs font-bold transition-colors ${
                    order.status === k
                      ? v.color
                      : "bg-white border-slate-200 text-slate-700 hover:border-cyan-400"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* AWB */}
          {(order.cargus_awbs?.length > 0 || order.cargus_awb) ? (
            <div className="rounded-xl bg-cyan-50 border border-cyan-100 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-cyan-700 font-semibold">
                    Cargus AWB
                    {order.cargus_awbs?.length > 1 && (
                      <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-cyan-700 text-white text-[10px] font-bold">
                        {order.cargus_awbs.length} colete
                      </span>
                    )}
                  </div>
                  {order.cargus_awbs?.length > 1 ? (
                    <div className="mt-1 space-y-0.5">
                      {order.cargus_awbs.map((a, i) => (
                        <div
                          key={a.barcode}
                          className="font-mono text-sm font-black text-slate-900 flex items-center gap-2"
                        >
                          <span className="text-[10px] text-cyan-700 font-sans font-bold w-8">
                            {i + 1}/{order.cargus_awbs.length}
                          </span>
                          <span>{a.barcode}</span>
                          <span className="text-[10px] text-slate-500 font-sans">
                            ({a.cod_amount} RON)
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="font-heading text-lg font-black text-slate-900 font-mono mt-1">
                      {order.cargus_awb}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <DownloadAwbButton orderNumber={order.order_number} awb={order.cargus_awb} />
                <ResendEmailButton orderNumber={order.order_number} />
              </div>
              <div className="mt-2">
                <GenerateAwbButton order={order} label="Regenerează AWB cu adresă corectată" />
              </div>
              {order.cod_supported === false && (
                <div className="text-xs text-amber-700 mt-3">
                  ⚠ COD nu a fost aplicat (contractul Cargus nu acceptă)
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
              <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">
                Cargus AWB
              </div>
              <div className="text-sm text-slate-700 mb-2">
                Această comandă nu are AWB Cargus. Verifică adresa clientului și generează-l manual.
              </div>
              {order.cargus_error && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2 mb-3 font-mono break-words">
                  {order.cargus_error}
                </div>
              )}
              <GenerateAwbButton order={order} label="Generează AWB acum" />
            </div>
          )}

          {/* Oblio Invoice */}
          {order.oblio_invoice?.number ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">
                    Factură Oblio
                  </div>
                  <div className="font-heading text-lg font-black text-slate-900 font-mono mt-1">
                    {order.oblio_invoice.series} {order.oblio_invoice.number}
                  </div>
                </div>
                {order.oblio_invoice.total && (
                  <div className="text-right">
                    <div className="text-[10px] uppercase text-emerald-700 font-semibold">Total</div>
                    <div className="font-bold text-emerald-700">{order.oblio_invoice.total} RON</div>
                  </div>
                )}
              </div>
              <DownloadInvoiceButton
                orderNumber={order.order_number}
                label={`${order.oblio_invoice.series}-${order.oblio_invoice.number}`}
              />
            </div>
          ) : (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
              <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">
                Factură Oblio
              </div>
              <div className="text-sm text-slate-700 mb-3">
                Această comandă nu are factură emisă încă.
              </div>
              <GenerateInvoiceButton
                orderNumber={order.order_number}
                onDone={onClose}
              />
              {order.oblio_error && (
                <div className="mt-2 text-[11px] text-rose-700">
                  Ultima eroare: {order.oblio_error}
                </div>
              )}
            </div>
          )}

          {/* Customer */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Client
            </div>
            <div className="space-y-2 text-sm">
              <div className="font-semibold text-slate-900">{c.fullName}</div>
              <a
                href={`tel:${c.phone}`}
                className="flex items-center gap-2 text-cyan-700 hover:text-cyan-800"
              >
                <Phone className="w-4 h-4" /> {c.phone}
              </a>
              {c.email && (
                <a
                  href={`mailto:${c.email}`}
                  className="flex items-center gap-2 text-cyan-700 hover:text-cyan-800"
                >
                  <Mail className="w-4 h-4" /> {c.email}
                </a>
              )}
              <div className="flex items-start gap-2 text-slate-700">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {c.address}, {c.city}, {c.county}
                  {c.postalCode ? `, ${c.postalCode}` : ""}
                </span>
              </div>
              {c.notes && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
                  📝 {c.notes}
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Produse
            </div>
            <div className="space-y-2">
              {(order.items || []).map((it, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100"
                >
                  <div className="text-sm">
                    <div className="font-semibold text-slate-900">{it.title}</div>
                    <div className="text-xs text-slate-500">
                      {it.qty} × {it.price} lei
                    </div>
                  </div>
                  <div className="font-bold text-slate-900 text-sm">
                    {(it.qty * it.price).toFixed(2)} lei
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
              <span className="font-semibold text-slate-700">Total</span>
              <span className="font-heading text-xl font-black text-emerald-600">
                {order.total} lei
              </span>
            </div>
          </div>

          {order.cargus_error && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-700">
              <strong>Eroare Cargus:</strong> {order.cargus_error}
            </div>
          )}

          {/* Danger zone — delete order */}
          <div className="pt-4 mt-2 border-t border-slate-200">
            <button
              onClick={() => onDelete && onDelete(order.order_number)}
              data-testid={`admin-delete-order-${order.order_number}`}
              className="w-full h-11 rounded-lg bg-white border border-rose-300 hover:bg-rose-600 hover:text-white hover:border-rose-600 text-rose-700 font-bold text-sm inline-flex items-center justify-center gap-2 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Șterge comanda permanent
            </button>
            <p className="mt-2 text-[11px] text-slate-500 text-center">
              Comanda va fi eliminată definitiv din baza de date.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
